// frontend/src/components/PlantaoAgenda.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Grid,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Stack
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import API_URL from '../config';

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const EMPTY_COMPROMISSO = { id: null, hora: '', titulo: '' };

function pad(n) {
  return String(n).padStart(2, '0');
}

function toISODate(y, m, d) {
  return `${y}-${pad(m + 1)}-${pad(d)}`;
}

export default function PlantaoAgenda({ token, user }) {
  const canEditPlantao = user?.sector === 'DEV' || user?.sector === 'SAF';

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-11
  const [plantoes, setPlantoes] = useState({}); // { 'YYYY-MM-DD': 'nome' }
  const [compromissosByDay, setCompromissosByDay] = useState({}); // { 'YYYY-MM-DD': [...] }
  const [colaboradores, setColaboradores] = useState([]);

  const [dialogDia, setDialogDia] = useState(null);
  const [plantaoSelected, setPlantaoSelected] = useState('');
  const [compromissoForm, setCompromissoForm] = useState(EMPTY_COMPROMISSO);
  const [saving, setSaving] = useState(false);

  const fetchPlantoes = useCallback(() => {
    if (!token) return;
    fetch(`${API_URL}/api/plantoes?ano=${year}&mes=${month + 1}`, {
      headers: { Authorization: 'Bearer ' + token }
    })
      .then(r => (r.ok ? r.json() : []))
      .then(rows => {
        const map = {};
        (rows || []).forEach(r => {
          map[r.dia] = r.atendente;
        });
        setPlantoes(map);
      })
      .catch(() => setPlantoes({}));
  }, [token, year, month]);

  const fetchCompromissos = useCallback(() => {
    if (!token) return;
    fetch(`${API_URL}/api/compromissos?ano=${year}&mes=${month + 1}`, {
      headers: { Authorization: 'Bearer ' + token }
    })
      .then(r => (r.ok ? r.json() : []))
      .then(rows => {
        const map = {};
        (rows || []).forEach(r => {
          if (!map[r.dia]) map[r.dia] = [];
          map[r.dia].push(r);
        });
        setCompromissosByDay(map);
      })
      .catch(() => setCompromissosByDay({}));
  }, [token, year, month]);

  useEffect(() => {
    fetchPlantoes();
    fetchCompromissos();
  }, [fetchPlantoes, fetchCompromissos]);

  useEffect(() => {
    if (!token || !canEditPlantao) return;
    fetch(`${API_URL}/api/plantoes/colaboradores`, {
      headers: { Authorization: 'Bearer ' + token }
    })
      .then(r => (r.ok ? r.json() : []))
      .then(data => setColaboradores(Array.isArray(data) ? data : []))
      .catch(() => setColaboradores([]));
  }, [token, canEditPlantao]);

  const changeMonth = delta => {
    let m = month + delta;
    let y = year;
    if (m < 0) {
      m = 11;
      y -= 1;
    } else if (m > 11) {
      m = 0;
      y += 1;
    }
    setMonth(m);
    setYear(y);
  };

  const goToday = () => {
    setYear(today.getFullYear());
    setMonth(today.getMonth());
  };

  const firstWeekday = new Date(year, month, 1).getDay(); // 0 = Domingo
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const openDialog = day => {
    const iso = toISODate(year, month, day);
    setPlantaoSelected(plantoes[iso] || '');
    setCompromissoForm(EMPTY_COMPROMISSO);
    setDialogDia(iso);
  };

  const closeDialog = () => {
    setDialogDia(null);
    setCompromissoForm(EMPTY_COMPROMISSO);
  };

  const isDialogSaturday = dialogDia
    ? new Date(`${dialogDia}T00:00:00`).getDay() === 6
    : false;

  const handleSavePlantao = () => {
    if (!dialogDia) return;
    setSaving(true);
    const url = `${API_URL}/api/plantoes/${dialogDia}`;
    const request = plantaoSelected
      ? fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
          },
          body: JSON.stringify({ atendente: plantaoSelected })
        })
      : fetch(url, {
          method: 'DELETE',
          headers: { Authorization: 'Bearer ' + token }
        });

    request
      .then(r => {
        if (!r.ok && r.status !== 204) throw new Error();
        fetchPlantoes();
      })
      .catch(() => alert('Erro ao salvar plantão.'))
      .finally(() => setSaving(false));
  };

  const handleSaveCompromisso = () => {
    if (!dialogDia || !compromissoForm.titulo.trim()) return;
    setSaving(true);
    const isEdit = Boolean(compromissoForm.id);
    const url = isEdit
      ? `${API_URL}/api/compromissos/${compromissoForm.id}`
      : `${API_URL}/api/compromissos`;

    fetch(url, {
      method: isEdit ? 'PUT' : 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token
      },
      body: JSON.stringify({
        dia: dialogDia,
        hora: compromissoForm.hora || null,
        titulo: compromissoForm.titulo.trim()
      })
    })
      .then(r => {
        if (!r.ok) throw new Error();
        setCompromissoForm(EMPTY_COMPROMISSO);
        fetchCompromissos();
      })
      .catch(() => alert('Erro ao salvar compromisso.'))
      .finally(() => setSaving(false));
  };

  const handleEditCompromisso = item => {
    setCompromissoForm({ id: item.id, hora: item.hora || '', titulo: item.titulo });
  };

  const handleDeleteCompromisso = id => {
    if (!window.confirm('Remover este compromisso?')) return;
    fetch(`${API_URL}/api/compromissos/${id}`, {
      method: 'DELETE',
      headers: { Authorization: 'Bearer ' + token }
    })
      .then(r => {
        if (!r.ok && r.status !== 204) throw new Error();
        fetchCompromissos();
      })
      .catch(() => alert('Erro ao remover compromisso.'));
  };

  const formattedDialogDate = dialogDia
    ? dialogDia.split('-').reverse().join('/')
    : '';

  const dialogCompromissos = dialogDia ? compromissosByDay[dialogDia] || [] : [];

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2
        }}
      >
        <Typography variant="h6">Agenda</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => changeMonth(-1)} size="small">
            <ChevronLeftIcon />
          </IconButton>
          <Typography sx={{ minWidth: 140, textAlign: 'center', fontWeight: 600 }}>
            {MONTHS[month]} {year}
          </Typography>
          <IconButton onClick={() => changeMonth(1)} size="small">
            <ChevronRightIcon />
          </IconButton>
          <Button size="small" onClick={goToday} sx={{ ml: 1 }}>
            Hoje
          </Button>
        </Box>
      </Box>

      <Grid container columns={7} spacing={1}>
        {WEEKDAYS.map(w => (
          <Grid key={w} size={1}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', textAlign: 'center', fontWeight: 700 }}
            >
              {w}
            </Typography>
          </Grid>
        ))}

        {cells.map((day, idx) => {
          const isSaturday = idx % 7 === 6;
          const iso = day ? toISODate(year, month, day) : null;
          const responsavel = iso ? plantoes[iso] : null;
          const compromissosDoDia = iso ? compromissosByDay[iso] || [] : [];
          const preview = compromissosDoDia.slice(0, 2);
          const extra = compromissosDoDia.length - preview.length;

          return (
            <Grid key={idx} size={1}>
              {day ? (
                <Paper
                  variant="outlined"
                  onClick={() => openDialog(day)}
                  sx={{
                    minHeight: { xs: 66, sm: 90 },
                    p: { xs: 0.75, sm: 1 },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    cursor: 'pointer',
                    bgcolor: isSaturday ? 'action.hover' : 'transparent',
                    borderColor: isSaturday ? 'primary.main' : 'divider',
                    transition: 'background-color 0.15s',
                    overflow: 'hidden',
                    '&:hover': { bgcolor: 'action.selected' }
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: isSaturday ? 700 : 400 }}
                  >
                    {day}
                  </Typography>

                  {isSaturday &&
                    (responsavel ? (
                      <Chip
                        label={responsavel}
                        size="small"
                        color="primary"
                        sx={{ alignSelf: 'flex-start', maxWidth: '100%' }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {canEditPlantao ? 'Plantão: definir…' : 'Sem plantonista'}
                      </Typography>
                    ))}

                  {preview.map(c => (
                    <Typography
                      key={c.id}
                      variant="caption"
                      noWrap
                      sx={{
                        bgcolor: 'background.default',
                        borderRadius: 0.5,
                        px: 0.5,
                        maxWidth: '100%'
                      }}
                    >
                      {c.hora ? `${c.hora} ` : ''}
                      {c.titulo}
                    </Typography>
                  ))}
                  {extra > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      +{extra} mais
                    </Typography>
                  )}
                </Paper>
              ) : (
                <Box sx={{ minHeight: { xs: 66, sm: 90 } }} />
              )}
            </Grid>
          );
        })}
      </Grid>

      {!canEditPlantao && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Apenas DEV/SAF podem definir ou alterar o responsável pelo plantão de sábado.
          Compromissos podem ser criados por qualquer colaborador.
        </Typography>
      )}

      <Dialog open={!!dialogDia} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>Agenda de {formattedDialogDate}</DialogTitle>
        <DialogContent dividers>
          {isDialogSaturday && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Plantão de sábado
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Responsável"
                  value={plantaoSelected}
                  onChange={e => setPlantaoSelected(e.target.value)}
                  disabled={!canEditPlantao}
                >
                  <MenuItem value="">— Sem responsável —</MenuItem>
                  {/* mantém o responsável atual na lista mesmo que o usuário tenha sido inativado */}
                  {(plantaoSelected && !colaboradores.includes(plantaoSelected)
                    ? [plantaoSelected, ...colaboradores]
                    : colaboradores
                  ).map(c => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
                {canEditPlantao && (
                  <Button
                    variant="outlined"
                    onClick={handleSavePlantao}
                    disabled={saving}
                    sx={{ flexShrink: 0 }}
                  >
                    Salvar plantão
                  </Button>
                )}
              </Stack>
              <Divider sx={{ mb: 2 }} />
            </>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Compromissos
          </Typography>

          {dialogCompromissos.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Nenhum compromisso marcado neste dia.
            </Typography>
          )}

          <Stack spacing={1} sx={{ mb: 2 }}>
            {dialogCompromissos.map(c => (
              <Box
                key={c.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: 'action.hover'
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {c.hora && <strong>{c.hora} — </strong>}
                    {c.titulo}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    por {c.criado_por}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <IconButton size="small" onClick={() => handleEditCompromisso(c)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteCompromisso(c.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {compromissoForm.id ? 'Editar compromisso' : 'Novo compromisso'}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              label="Hora"
              type="time"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={compromissoForm.hora}
              onChange={e =>
                setCompromissoForm(f => ({ ...f, hora: e.target.value }))
              }
              sx={{ width: { xs: '100%', sm: 140 } }}
            />
            <TextField
              label="Título"
              size="small"
              fullWidth
              value={compromissoForm.titulo}
              onChange={e =>
                setCompromissoForm(f => ({ ...f, titulo: e.target.value }))
              }
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleSaveCompromisso}
              disabled={saving || !compromissoForm.titulo.trim()}
              sx={{ flexShrink: 0 }}
            >
              {compromissoForm.id ? 'Atualizar' : 'Adicionar'}
            </Button>
            {compromissoForm.id && (
              <Button onClick={() => setCompromissoForm(EMPTY_COMPROMISSO)}>
                Cancelar
              </Button>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
